import React, { useRef, useState } from "react";
import dayjs from "dayjs";
import { Alert, Box, Button, CircularProgress, Stack, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { CardWrapper } from "../../ui";
import SecuritySuggest from "./SecuritySuggest";
import DecisionRail, { VerdictDetails } from "./DecisionRail";
import EvidenceSections from "./EvidenceSections";
import {
  attestRegMPreClear,
  checkRegMPreClear,
  downloadRegMPreClearPdf,
} from "../../../services/Axios";
import {
  canAttestResult,
  errorMessage,
  railState,
  savePdfBlob,
  validatePreClearForm,
  canSubmitCheck,
  submitExact,
  defaultPricingDate,
  wildcardProblem,
  fieldErrorText,
} from "./regmFormat";
import { TOUCH, focusRing } from "./regmStyles";
import HELP from "./helpText";
import HelpTip from "./HelpTip";

const ISO_DATE = "YYYY-MM-DD";
const RAIL_WIDTH = 360;
// A field with its info tooltip beside it (top aligned with the input, so an error under it never moves the icon).
const FieldWithTip = ({ text, label, grow = false, children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", minWidth: 0, ...(grow && { flex: 1 }) }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    <Box sx={{ mt: 0.75 }}>
      <HelpTip text={text} label={label} />
    </Box>
  </Box>
);

const PreClearTab = () => {
  const [securityText, setSecurityText] = useState("");
  const [pricingDate, setPricingDate] = useState(() => defaultPricingDate(dayjs()));
  const [filingDate, setFilingDate] = useState(null);
  const [errors, setErrors] = useState({});
  const [checking, setChecking] = useState(false);
  const [attesting, setAttesting] = useState(false);
  const [exactChosen, setExactChosen] = useState(false);
  // The mode the displayed result was computed in; attest must repeat it.
  const [checkedExact, setCheckedExact] = useState(false);
  const [result, setResult] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [checkError, setCheckError] = useState(null);
  const [attestError, setAttestError] = useState(null);
  const [record, setRecord] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  // Incremented on every input change so a check that returns after an edit is discarded.
  const requestId = useRef(0);
  const attestInFlight = useRef(false);
  // Changing the key remounts the security box, which clears its text and suggestions (New check).
  const [formKey, setFormKey] = useState(0);
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));

  const resetResult = () => {
    requestId.current += 1;
    setChecking(false);
    setAttesting(false);
    attestInFlight.current = false;
    setErrors({});
    setResult(null);
    setAccepted(false);
    setCheckError(null);
    setAttestError(null);
    setRecord(null);
    setDownloadError(null);
  };

  const handleSecurityInput = (value) => {
    setSecurityText(value);
    setExactChosen(false);
    resetResult();
  };
  const handleSecurityChoose = (value) => {
    setSecurityText(value);
    setExactChosen(true);
    resetResult();
  };
  const handlePricingDate = (value) => {
    setPricingDate(value);
    resetResult();
  };
  const handleFilingDate = (value) => {
    setFilingDate(value);
    resetResult();
  };

  // text and exact are explicit so Enter right after choosing a suggestion never reads stale state.
  const handleCheck = async (text = securityText, exactArg) => {
    const exact = submitExact(exactArg, exactChosen);
    if (!canSubmitCheck({ text, checking, attesting })) return;
    const validation = validatePreClearForm({
      security: text,
      pricingDate,
      filingDate,
    });
    setErrors(validation);
    if (Object.keys(validation).length > 0) return;
    resetResult();
    const myId = requestId.current;
    setChecking(true);
    try {
      const data = await checkRegMPreClear({
        exact,
        security: text.trim(),
        pricing_date: pricingDate.format(ISO_DATE),
        filing_date: filingDate ? filingDate.format(ISO_DATE) : null,
      });
      if (myId !== requestId.current) return;
      setResult(data);
      setCheckedExact(exact);
    } catch (error) {
      if (myId !== requestId.current) return;
      setCheckError(errorMessage(error));
    } finally {
      if (myId === requestId.current) setChecking(false);
    }
  };

  const handleAttest = async () => {
    if (attestInFlight.current) return;
    if (!canAttestResult({ result, accepted, attesting, record })) return;
    attestInFlight.current = true;
    const myId = requestId.current;
    setAttesting(true);
    setAttestError(null);
    try {
      const data = await attestRegMPreClear({
        security: result.security,
        pricing_date: result.pricing_date,
        filing_date: result.filing_date,
        attestation_version: result.attestation_version,
        accepted: true,
        exact: checkedExact,
      });
      if (myId !== requestId.current) return;
      setRecord(data);
    } catch (error) {
      if (myId !== requestId.current) return;
      setAttestError(errorMessage(error));
    } finally {
      if (myId === requestId.current) {
        attestInFlight.current = false;
        setAttesting(false);
      }
    }
  };

  // Clears the form for the next check: empty security, pricing date today, no filing date, typed mode.
  const handleNewCheck = () => {
    resetResult();
    setSecurityText("");
    setExactChosen(false);
    setCheckedExact(false);
    setPricingDate(defaultPricingDate(dayjs()));
    setFilingDate(null);
    setFormKey((k) => k + 1);
  };

  const handleDownload = async () => {
    setDownloadError(null);
    try {
      const blob = await downloadRegMPreClearPdf(record.id);
      savePdfBlob(blob, record.id);
    } catch (error) {
      setDownloadError(errorMessage(error));
    }
  };

  const securityProblem = wildcardProblem(securityText);
  const rail = railState({ result, accepted, attesting, record, checking });
  const showRail = rail !== "hidden";

  const decisionRail = (
    <DecisionRail
      state={rail}
      result={result}
      record={record}
      accepted={accepted}
      onAccept={setAccepted}
      onAttest={handleAttest}
      attestError={attestError}
      onDownload={handleDownload}
      downloadError={downloadError}
      onNewCheck={handleNewCheck}
      compact={compact}
    />
  );

  return (
    <Stack spacing={1}>
      <CardWrapper>
        <Stack spacing={2}>
          <FieldWithTip text={HELP.security} label="security search">
          <SecuritySuggest
            key={formKey}
            autoFocus={formKey > 0}
            onTextChange={handleSecurityInput}
            onChoose={handleSecurityChoose}
            onSubmit={handleCheck}
            disabled={attesting}
            ariaLabel="Security ticker, Bloomberg symbol or ISIN"
            placeholder="Ticker, Bloomberg symbol or ISIN"
            error={Boolean(securityProblem)}
            helperText={securityProblem}
          />
          </FieldWithTip>
          {errors.security && <Alert severity="warning">{errors.security}</Alert>}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {/* Below sm the fields stack and fill the width (Check full width); from sm one row, top aligned
                so an error under a date never stretches the Check button. */}
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", sm: "flex-start" }}
            >
              <FieldWithTip text={HELP.pricingDate} label="pricing date">
              <DatePicker
                label="Pricing date"
                value={pricingDate}
                onChange={handlePricingDate}
                disabled={attesting}
                slotProps={{
                  textField: {
                    size: "small",
                    required: true,
                    error: Boolean(errors.pricingDate),
                    helperText: fieldErrorText(errors.pricingDate),
                  },
                }}
              />
              </FieldWithTip>
              <FieldWithTip text={HELP.filingDate} label="filing date">
              <DatePicker
                label="Filing date (optional)"
                value={filingDate}
                onChange={handleFilingDate}
                disabled={attesting}
                slotProps={{
                  textField: {
                    size: "small",
                    error: Boolean(errors.filingDate),
                    helperText: fieldErrorText(errors.filingDate),
                  },
                  field: { clearable: true },
                }}
              />
              </FieldWithTip>
              <Button
                variant="contained"
                color="secondary"
                onClick={() => handleCheck()}
                disabled={checking || attesting || Boolean(securityProblem)}
                startIcon={checking ? <CircularProgress size={20} /> : null}
                sx={{ minHeight: TOUCH, flexShrink: 0, ...focusRing }}
              >
                {checking ? "Checking..." : "Check"}
              </Button>
            </Stack>
          </LocalizationProvider>
          {checkError && <Alert severity="error">{checkError}</Alert>}
        </Stack>
      </CardWrapper>

      {showRail && (
        <CardWrapper sx={{ pb: { xs: 3, md: 1 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "minmax(0, 1fr)", md: `minmax(0, 1fr) ${RAIL_WIDTH}px` },
              columnGap: 3,
              alignItems: "start",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <EvidenceSections result={result}>
                {/* Below md the rail is a slim bottom bar, so the details it would hold sit here. */}
                {compact && rail !== "confirmed" && <VerdictDetails result={result} />}
              </EvidenceSections>
            </Box>
            {!compact && decisionRail}
          </Box>
        </CardWrapper>
      )}
      {/* Below md: a direct child of this stack, last in the flow and sticky to the bottom, so at the
          end of the page it sits below the content instead of covering it. */}
      {showRail && compact && decisionRail}
    </Stack>
  );
};

export default PreClearTab;
